<template>
  <div class="col-sm-12">
    <div class="card">
      <!-- Single row for all controls -->
      <div class="dropdown-item page-item">
        <div class="page-grid">
          <!-- Search Bar -->
          <div class="page-search-container dropdown-item">
            <input
                type="text"
                placeholder="Search templates by name, module"
                class="search-input"
                v-model="searchFilter"
            />
          </div>

          <!-- Create Button -->
          <button type="button" class="btn button-default new-item-btn" @click="addNewTemplate">
            Add New Template
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <div class="table-responsive" v-if="templateList.length > 0">
        <table class="table">
          <thead>
          <tr class="border-bottom-primary">
            <th scope="col">Document Name</th>
            <th scope="col">Category</th>
            <th scope="col">Module</th>
            <th scope="col">Sub Module</th>
            <th scope="col">Required</th>
            <th scope="col">Status</th>
            <th scope="col">Document</th>
            <th scope="col">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="template in filteredTemplates" :key="template.id">
            <td>{{ template.document_name }}</td>
            <td>{{ template.category_name }}</td>
            <td>{{ template.module_name }}</td>
            <td>{{ template.submodule_name }}</td>
            <td>
                <span :class="{
                  'status-label status-yes': template.is_required == 1,
                  'status-label required-no': template.is_required == 0
                }">
                  {{ template.is_required == 1 ? 'Required' : 'Optional' }}
                </span>
            </td>
            <td>
                <span :class="{
                  'status-label status-yes': template.is_active == 1,
                  'status-label status-no': template.is_active == 0
                }">
                  {{ template.is_active == 1 ? 'Active' : 'Inactive' }}
                </span>
            </td>
            <td>
              <a
                v-if="template.template_file_url"
                :href="template.template_file_url"
                target="_blank"
                rel="noopener"
                class="document-btn"
                :title="template.template_file_name"
              >
                <i class="fa fa-external-link-alt"></i>
                <span>View</span>
              </a>
              <span v-else class="no-document">&mdash;</span>
            </td>
           <td>
            <div class="action-icons">
              <i class="fa fa-edit action-icon edit-icon" title="Edit" @click="editDetails(template.id)"></i>

              <i
                :class="template.is_active == 1
                  ? 'fa fa-trash action-icon deactivate-icon'
                  : 'fa fa-undo action-icon activate-icon'"
                :title="template.is_active == 1 ? 'Deactivate' : 'Activate'"
                @click="confirmToggleStatus(template)"
              ></i>
            </div>
          </td>
          </tr>
          <tr v-if="filteredTemplates.length === 0">
            <td colspan="8" class="text-center">No document templates found matching your search</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="!loading && templateList.length === 0" class="no-data-message">
        No document templates found.
      </div>
    </div>
  </div>

  <!-- Status Confirmation Modal -->
  <div v-if="showStatusModal" class="status-modal-overlay">
    <div class="status-modal">
      <div class="status-modal-icon" :class="pendingStatus === 1 ? 'activate' : 'deactivate'">
        <i
          :class="pendingStatus === 1
            ? 'fa fa-check-circle'
            : 'fa fa-exclamation-triangle'"
        ></i>
      </div>

      <h4>
        {{ pendingStatus === 1 ? 'Activate Template' : 'Deactivate Template' }}
      </h4>

      <p>
        Are you sure you want to
        <strong>{{ pendingStatus === 1 ? 'activate' : 'deactivate' }}</strong>
        "{{ selectedTemplate?.document_name }}"?
      </p>

      <div class="status-modal-actions">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeStatusModal"
        >
          Cancel
        </button>

        <button
          type="button"
          :class="pendingStatus === 1 ? 'btn btn-success' : 'btn btn-danger'"
          @click="confirmStatusChange"
          :disabled="statusUpdating"
        >
          <i v-if="statusUpdating" class="fa fa-spinner fa-spin"></i>
          {{ statusUpdating ? 'Updating...' : (pendingStatus === 1 ? 'Activate' : 'Deactivate') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { getDocumentTemplateList, updateDocumentTemplateStatus } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: 'DocumentTemplateList',
  data() {
    return {
      loading: false,
      token: localStorage.getItem("token_set") || "",
      profile_id: localStorage.getItem("profile_id"),
      templateList: [],
      searchFilter: "",

      showStatusModal: false,
      selectedTemplate: null,
      pendingStatus: null,
      statusUpdating: false,
    }
  },

  computed: {
    filteredTemplates() {
      const search = this.searchFilter.toLowerCase();
      return [...this.templateList]
          .filter(template =>
              (template.document_name || '').toLowerCase().includes(search) ||
              (template.module_name || '').toLowerCase().includes(search) ||
              (template.sub_module_name || '').toLowerCase().includes(search)
          );
    }
  },

  methods: {
    async getTemplates() {
      try {
        this.loading = true;
        const response = await getDocumentTemplateList(this.token);
        this.templateList = response.data?.data || [];
        console.log('Fetched Document Templates:', this.templateList);
      } catch (error) {
        console.error("Error fetching document template list:", error);
        this.templateList = [];
      } finally {
        this.loading = false;
      }
    },

    addNewTemplate() {
      this.$router.push({ path: '/add-document-template' });
    },

    editDetails(id) {
      this.$router.push({ path: `/add-document-template/${id}` });
    },

    confirmToggleStatus(template) {
      this.selectedTemplate = template;
      this.pendingStatus = template.is_active == 1 ? 0 : 1;
      this.showStatusModal = true;
    },

    closeStatusModal() {
      this.showStatusModal = false;
      this.selectedTemplate = null;
      this.pendingStatus = null;
    },

    async confirmStatusChange() {
      if (!this.selectedTemplate || this.pendingStatus === null) {
        return;
      }

      const template = this.selectedTemplate;
      const newStatus = this.pendingStatus;

      await this.toggleStatus(template, newStatus);
    },

    async toggleStatus(template, newStatus) {
      try {
        this.statusUpdating = true;

        const response = await updateDocumentTemplateStatus(
          template.id,
          this.token,
          { is_active: newStatus }
        );

        if (response.data.status === "success") {
          template.is_active = newStatus;

          this.toast.success(
            response.data.message ||
            `Template ${newStatus === 1 ? 'activated' : 'deactivated'} successfully.`
          );

          this.closeStatusModal();
        } else {
          this.toast.error(
            response.data.message || "Failed to update status."
          );
        }
      } catch (error) {
        console.error("Error updating document template status:", error);

        this.toast.error(
          error.response?.data?.message ||
          "Failed to update document template status."
        );
      } finally {
        this.statusUpdating = false;
      }
    },
  },
  
  setup() {
    const router = useRouter();
    const toast = useToast();
    return { router, toast };
  },

  mounted() {
    this.getTemplates();
  }
};
</script>

<style scoped lang="scss">
.page-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 15px;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}

.page-search-container {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;
  min-width: 250px;
}

.search-input:hover {
  border-color: #0056b3;
}

.new-item-btn {
  margin-left: auto;
  padding: 10px 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.card {
  padding: 10px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

i:hover {
  color: #0056b3;
  transform: scale(1.2);
}

i:active {
  transform: scale(1.1);
}

.status-label {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 5px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 90px;
  max-width: 90px;
  text-align: center;
}

.status-yes {
  background-color: #069E27;
  color: #fff;
  border-radius: 4px;
}

.status-no {
  background-color: red;
  color: #fff;
  border-radius: 4px;
}

.required-no {
  background-color: rgb(255, 187, 0);
  color: #fff;
  border-radius: 4px;
}

.document-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background-color: #eaf2ff;
  color: #1c66ad;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid #cfe1fb;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.document-btn i {
  font-size: 12px;
}

.document-btn:hover {
  background-color: #1c66ad;
  color: #fff;
  border-color: #1c66ad;
  box-shadow: 0 3px 8px rgba(28, 102, 173, 0.3);
  transform: translateY(-1px);
}

.document-btn:active {
  transform: translateY(0);
  box-shadow: none;
}

.no-document {
  color: #9ca3af;
  font-size: 14px;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

.text-center {
  text-align: center;
}

/* Focus states */
input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.button-default {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.button-default:hover {
  color: #fff;
  background-color: #0056b3;
  border-color: #004085;
}

.fa-trash {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  color: #dc2626;
}

.fa-undo {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  color: #069e27;
}

.action-icons {
  display: flex;
  align-items: center;
  gap: 15px;
}

.action-icon {
  cursor: pointer;
  font-size: 16px;
  transition: color 0.3s, transform 0.3s;
}

.edit-icon {
  color: #007bff;
}

.edit-icon:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.deactivate-icon {
  color: #dc2626;
}

.deactivate-icon:hover {
  color: #b91c1c;
  transform: scale(1.2);
}

.activate-icon {
  color: #069e27;
}

.activate-icon:hover {
  color: #047a1e;
  transform: scale(1.2);
}

.status-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.status-modal {
  background: #fff;
  width: 420px;
  max-width: 90%;
  border-radius: 10px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
}

.status-modal-icon {
  width: 55px;
  height: 55px;
  margin: 0 auto 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.status-modal-icon.activate {
  background: #e8f7ed;
  color: #069e27;
}

.status-modal-icon.deactivate {
  background: #fdecec;
  color: #dc2626;
}

.status-modal h4 {
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
}

.status-modal p {
  margin-bottom: 25px;
  color: #6c757d;
  font-size: 14px;
}

.status-modal-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.status-modal-actions button {
  min-width: 100px;
  padding: 8px 18px;
  border-radius: 5px;
  border: none;
}

.btn-success {
  background-color: #069e27;
  color: #fff;
}

.btn-success:hover {
  background-color: #047a1e;
}

.btn-danger {
  background-color: #dc2626;
  color: #fff;
}

.btn-danger:hover {
  background-color: #b91c1c;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #545b62;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-grid {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .page-search-container {
    width: 100%;
  }

  .search-input {
    min-width: 100%;
  }

  .new-item-btn {
    margin-left: 0;
    width: 100%;
  }
}
</style>